import { DiscountLocation } from 'client/Interfaces/Draft/v1/DraftInterface'
import { EditedDraftV1Interface } from 'client/Interfaces/Draft/v1/EditedDraftV1Interface'

import { DocumentType, DocumentTypes, DraftInterface } from '../../Interfaces/Draft/DraftInterface'
import { DraftFieldsInterface } from '../../Interfaces/Draft/FieldsInterface'
import { defFields, LineInterface } from '../../Interfaces/Draft/LineInterface'
import { ObjectNumber } from '../ObjectNumber'
import { Type } from '../Type'
import { calculateDeposit } from './paymentScheduleFunctions'

const UNIT_PRICE_DECIMAL_PLACES = 10
const ZERO_VAT_INVOICE_TYPE_IDS = ['2', '3', '6', '18', '127', '134', '135', '136', '137', '138', '139']

export const reCalculatePreciseUnitPrice = (line: LineInterface): LineInterface => {
  const newLine = { ...line }
  if (line.defField === defFields.TOTAL) {
    newLine.unitPrice = new ObjectNumber(line.grossTotal)
      .divide(1 + (line?.vatPct ?? 0))
      .toNumber(UNIT_PRICE_DECIMAL_PLACES)
  }
  return newLine
}

const calculateVatAfterDiscountPerLine = (line: LineInterface, discountValue): number => {
  return (line?.vatPct ?? 0) * (line.unitPrice * line.quantity * (1 - discountValue / 100))
}

/**
 *
 *
 * @export
 * @param {DraftInterface} payload
 * @returns
 */
export function calculateValuesForDraft(payload: DraftInterface): DraftInterface {
  const lines = payload.lines
  let grossTotal = new ObjectNumber()
  let netTotal = new ObjectNumber()
  let subTotal = new ObjectNumber()
  let totalTax = new ObjectNumber()
  let totalDiscount = new ObjectNumber()
  const totalWork = new ObjectNumber()

  const values = [grossTotal, netTotal, subTotal, totalTax, totalDiscount, totalWork]
  values.map((v) => {
    v.reset()
  })

  const vatMap = {}
  const pushVAT = (vat: number, amount: number) => {
    if (!vatMap[vat]) {
      vatMap[vat] = { amount: ObjectNumber.fromNumber(0), percentage: vat }
    }

    vatMap[vat].amount.add(amount)
  }

  for (const line of lines) {
    if (Type.isInvoiceLine(line)) {
      totalTax.add(line.taxTotal)
      subTotal.add(line.grossTotal)
      grossTotal.add(line.grossTotal)
      netTotal.add(line.netTotal)

      if (line.work) {
        totalWork.add(line.grossTotal)
      }
      pushVAT(line?.vatPct ?? 0, line.taxTotal)
      if (payload.fields.discountEnabled && payload.fields.discountLocation?.value === 'perLine') {
        totalDiscount.add(line.totalDiscount)
      }
    }
  }
  const vatArray = []
  for (const i in vatMap) {
    vatMap[i].amount = vatMap[i].amount.toNumber()
    vatArray.push(vatMap[i])
  }

  const ratio = grossTotal.copy().divide(netTotal)
  if (
    payload.fields.discountEnabled &&
    payload.fields.discountEnabled.value === true &&
    payload.fields.discountAmount &&
    payload.fields.discountAmount.value > 0 &&
    payload.fields.discountLocation &&
    payload.fields.discountLocation.value === 'perTotal'
  ) {
    if (payload.fields.discountType.value === 'amount') {
      totalDiscount = totalDiscount.copy().add(payload.fields.discountAmount.value)
      const originalTotalTax = totalTax.copy()
      let vatRatio
      if (payload.fields.discountVAT.value === 'preVAT') {
        subTotal = netTotal.copy()
        payload.document === DocumentTypes.CREDITNOTE
          ? (netTotal = netTotal.copy().add(totalDiscount))
          : (netTotal = netTotal.copy().subtract(totalDiscount))
        grossTotal = netTotal.copy().multiply(ratio)
        totalTax = grossTotal.copy().subtract(netTotal)
      } else {
        subTotal = grossTotal.copy()
        payload.document === DocumentTypes.CREDITNOTE
          ? (grossTotal = grossTotal.copy().add(totalDiscount))
          : (grossTotal = grossTotal.copy().subtract(totalDiscount))
        netTotal = grossTotal.copy().divide(ratio)
        totalTax = grossTotal.copy().subtract(netTotal)
      }

      // update vat array to match discounted values
      vatArray.forEach((v) => {
        vatRatio = originalTotalTax.toNumber() !== 0 ? v.amount / originalTotalTax.toNumber() : 0
        v.amount = new ObjectNumber(totalTax.copy().multiply(vatRatio).toNumber()).toNumber(2)
      })
    } else {
      // percentage
      if (payload.fields.discountVAT.value === 'preVAT') {
        // when calculate preVAT, subTotal is excluding vat and equal to origin net total
        subTotal = netTotal.copy()
        totalDiscount = netTotal.copy().divide(100).multiply(payload.fields.discountAmount.value)
        netTotal = netTotal.copy().subtract(totalDiscount)
        // calculate tax total based on each line after discount
        const vatsMap = lines.map((line) =>
          Type.isInvoiceLine(line) ? calculateVatAfterDiscountPerLine(line, payload.fields.discountAmount.value) : 0,
        )
        const vats = vatsMap.reduce((previous, current) => previous + current)
        totalTax.reset()
        totalTax = totalTax.copy().add(vats)
        grossTotal = netTotal.copy().add(totalTax)
      } else {
        subTotal = grossTotal.copy()
        totalDiscount = grossTotal.copy().divide(100).multiply(payload.fields.discountAmount.value)
        grossTotal = grossTotal.copy().subtract(totalDiscount)
        // netTotal is calculated by gross total radio
        netTotal = grossTotal.copy().divide(ratio)
        totalTax = grossTotal.copy().subtract(netTotal)
      }
      // update vat array to match discounted values
      vatArray.forEach((v) => {
        v.amount = ObjectNumber.fromNumber(v.amount)
          .multiply(
            ObjectNumber.fromNumber(1).subtract(
              ObjectNumber.fromNumber(payload.fields.discountAmount.value).divide(100),
            ),
          )
          .toNumber()
      })
    }
  }

  payload.summary.taxSummary = vatArray.sort((a, b) => {
    return a.percentage - b.percentage
  })
  payload.summary.workTotal = totalWork.toNumber()
  if (payload.fields.deduction) {
    payload.summary.reductionTotal = totalWork
      .copy()
      .multiply((payload.fields.deduction.value || 0) / 100)
      .toNumber()
  }
  payload.summary.subTotal = subTotal.toNumber()
  payload.summary.discountTotal = totalDiscount.toNumber()
  payload.summary.grossTotal = grossTotal.toNumber()
  payload.summary.taxTotal = totalTax.toNumber()
  payload.summary.netTotal = netTotal.toNumber()
  payload = calculateDeposit(payload)
  return payload
}

export const calculateValuesForDraftV1 = (payload: EditedDraftV1Interface): EditedDraftV1Interface => {
  const lines = payload.lines
  let grossTotal = new ObjectNumber()
  let netTotal = new ObjectNumber()
  let totalTax = new ObjectNumber()
  let totalDiscount = new ObjectNumber()
  let subTotal = new ObjectNumber()

  const values = [grossTotal, netTotal, subTotal, totalTax, totalDiscount]
  values.map((v) => {
    v.reset()
  })

  for (const line of lines) {
    if (Type.isInvoiceLine(line)) {
      totalTax.add(line.taxTotal)
      grossTotal.add(line.grossTotal)
      netTotal.add(line.netTotal)
      totalDiscount.add(line.totalDiscount)
    }
  }

  const ratio = grossTotal.copy().divide(netTotal)

  if (
    payload.config.fields.discountEnabled &&
    payload.config.fields.discountEnabled.value === true &&
    payload.discount &&
    payload.discount.amount &&
    payload.discount.amount > 0 &&
    payload.discount.location === 'perTotal'
  ) {
    if (payload.discount.type === 'amount') {
      totalDiscount = totalDiscount.copy().add(payload.discount.amount)
      subTotal = netTotal.copy()
      if (payload.discount.vat === 'preVAT') {
        netTotal = netTotal.copy().subtract(totalDiscount)
        grossTotal = netTotal.copy().multiply(ratio)
        totalTax = grossTotal.copy().subtract(netTotal)
      } else {
        subTotal = grossTotal.copy()
        grossTotal = grossTotal.copy().subtract(totalDiscount)
        netTotal = grossTotal.copy().divide(ratio)
        totalTax = grossTotal.copy().subtract(netTotal)
      }
    } else {
      // percentage
      if (payload.discount.vat === 'preVAT') {
        // when calculate preVAT, subTotal is excluding vat and equal to origin net total
        subTotal = netTotal.copy()
        totalDiscount = netTotal.copy().divide(100).multiply(payload.discount.amount)
        netTotal = netTotal.copy().subtract(totalDiscount)
        // calculate tax total based on each line after discount
        const vatsMap = lines.map((line) =>
          Type.isInvoiceLine(line) ? calculateVatAfterDiscountPerLine(line, payload.discount.amount) : 0,
        )
        const vats = vatsMap.reduce((previous, current) => previous + current)
        totalTax.reset()
        totalTax = totalTax.copy().add(vats)
        grossTotal = netTotal.copy().add(totalTax)
      } else {
        subTotal = grossTotal.copy()
        totalDiscount = grossTotal.copy().divide(100).multiply(payload.discount.amount)
        grossTotal = grossTotal.copy().subtract(totalDiscount)
        netTotal = grossTotal.copy().divide(ratio)
        totalTax = grossTotal.copy().subtract(netTotal)
      }
    }
  }

  if (payload.discount) {
    payload.discount.total = totalDiscount.toNumber()
  }
  payload.summary.foreignDiscountTotal = totalDiscount.toNumber()
  payload.summary.foreignTaxTotal = totalTax.toNumber()
  payload.summary.foreignGrossTotal = grossTotal.toNumber()
  payload.summary.foreignNetTotal = netTotal.toNumber()
  payload.summary.foreignSubTotal = subTotal.toNumber()
  return payload
}

const perLineDiscountEnabled = (fields) => {
  return (
    fields &&
    fields.discountEnabled &&
    fields.discountEnabled.value &&
    fields.discountLocation &&
    fields.discountLocation.value === 'perLine'
  )
}

export const perLineDiscountSwicthDiscountColumns = (
  enabled: boolean,
  location: DiscountLocation,
  columns: Array<keyof LineInterface>,
): Array<keyof LineInterface> => {
  if (!enabled) {
    const newColumns = removeDiscountColumn(columns)
    return newColumns
  }
  if (location === 'perLine' && columns.indexOf('discount') <= -1) {
    const newColumns = [...columns, 'discount'] as Array<keyof LineInterface>
    return newColumns
  } else if (location === 'perTotal') {
    const newColumns = removeDiscountColumn(columns)
    return newColumns
  }
}

const removeDiscountColumn = (columns: Array<keyof LineInterface>) => {
  const newColumns = [...columns]
  const discountIndex = columns.indexOf('discount')
  if (discountIndex > -1) {
    newColumns.splice(discountIndex, 1)
  }
  return newColumns
}

const percentDiscountType = (fields) => {
  return fields.discountType && (fields.discountType.value === 'percent' || fields.discountType.value === 'percentage')
}

const amountDiscountType = (fields) => {
  return fields.discountType && fields.discountType.value === 'amount'
}

const discountAppliedPreVAT = (fields) => {
  return fields.discountVAT && fields.discountVAT.value === 'preVAT'
}

const discountAppliedPostVAT = (fields) => {
  return fields.discountVAT && fields.discountVAT.value === 'postVAT'
}

const amountDiscountTypeAndAppliedPostVAT = (fields) => {
  return amountDiscountType(fields) && discountAppliedPostVAT(fields)
}

const amountDiscountTypeAndAppliedPreVAT = (fields) => {
  return amountDiscountType(fields) && discountAppliedPreVAT(fields)
}

const percentDiscountTypeAndAppliedPreVAT = (fields) => {
  return percentDiscountType(fields) && discountAppliedPreVAT(fields)
}

const percentDiscountTypeAndAppliedPostVAT = (fields) => {
  return percentDiscountType(fields) && discountAppliedPostVAT(fields)
}

const applyDiscount = (total, discount, document) => {
  document === DocumentTypes.CREDITNOTE ? total.add(discount) : total.subtract(discount)
}

const defFieldIsPrice = (line: LineInterface): boolean => {
  const pricingModel = line.pricingModel || line.defField
  return Boolean(pricingModel) && pricingModel.includes('PRICE')
}

/**
 *
 * @param line
 * @param fields
 * @param document
 */
const calculateLineValuesForDefFieldGrossTotal = (
  line: LineInterface,
  fields: DraftFieldsInterface,
  document?: DocumentType,
) => {
  let vat
  let unitPrice
  let discountAmount
  const one = new ObjectNumber(1)
  const discount = ObjectNumber.fromNumber(line.discount)
  const total = new ObjectNumber(line.grossTotal)

  if (line.quantity === 0) {
    line.quantity = 1
  }

  if (perLineDiscountEnabled(fields) && discount > 0) {
    // calculate unit price and vat with discount
    vat = total.copy().subtract(total.copy().divide(1 + (line?.vatPct ?? 0)))
    discountAmount = discount.copy()
    unitPrice = total.copy()

    if (amountDiscountTypeAndAppliedPostVAT(fields)) {
      document === DocumentTypes.CREDITNOTE ? unitPrice.subtract(discount.copy()) : unitPrice.add(discount.copy())
      unitPrice.divide(
        one
          .copy()
          .add(line?.vatPct ?? 0)
          .multiply(line.quantity),
      )
    }

    if (amountDiscountTypeAndAppliedPreVAT(fields)) {
      document === DocumentTypes.CREDITNOTE ? unitPrice.subtract(vat.copy()) : unitPrice.add(vat.copy())
      unitPrice.add(discount.copy()).divide(line.quantity)
    }

    if (percentDiscountType(fields)) {
      unitPrice = total.copy().divide(
        one
          .copy()
          .add(line?.vatPct ?? 0)
          .multiply(one.copy().subtract(discount.copy().divide(100)))
          .multiply(line.quantity),
      )

      const fullLineValue = unitPrice
        .copy()
        .multiply(line.quantity)
        .multiply(one.copy().add(line?.vatPct ?? 0))

      if (discountAppliedPostVAT(fields)) {
        discountAmount = fullLineValue.copy().subtract(total)
      }

      if (discountAppliedPreVAT(fields)) {
        discountAmount = fullLineValue
          .copy()
          .subtract(total)
          .divide(one.copy().add(line?.vatPct ?? 0))
      }
    }
  } else {
    // no discount at all or discount per draft
    unitPrice = total.copy().divide(ObjectNumber.fromNumber(line.quantity).multiply(1 + (line?.vatPct ?? 0)))
    vat = total.copy().subtract(total.copy().divide(1 + (line?.vatPct ?? 0)))
  }
  return { total, vat, unitPrice, discountAmount }
}

/**
 *
 * @param line
 * @param fields
 * @param document
 */
const calculateLineValues = (line: LineInterface, fields: DraftFieldsInterface, document?: DocumentType) => {
  let vat
  let total
  let discountAmount
  const one = new ObjectNumber(1)
  const discount = ObjectNumber.fromNumber(line.discount)
  const unitPrice = new ObjectNumber(line.unitPrice)
  const lineTotalPreVAT = ObjectNumber.fromNumber(ObjectNumber.fromNumber(line.quantity).multiply(unitPrice).toNumber())
  discountAmount = discount.copy()
  return {
    when: {
      hasDiscountAmount: {
        postVAT: {
          defFieldIsPrice: () => {
            // grossTotal = ROUND((unitPrice x quantity x ( 1 + VAT%)) - discount)
            vat = lineTotalPreVAT.copy().multiply(line?.vatPct ?? 0)
            total = lineTotalPreVAT.copy().add(vat)
            applyDiscount(total, discount, document)
            vat = total.copy().subtract(total.copy().divide(1 + (line?.vatPct ?? 0)))
            return { total, vat, unitPrice, discountAmount }
          },
          defFieldIsTotal: () => {
            // grossTotal = ( ROUND((unitPrice x ( 1 + VAT%))) - (discount / quantity) ) x quantity
            vat = lineTotalPreVAT.copy().multiply(line?.vatPct ?? 0)
            const unitPriceWithVAT = unitPrice.copy().multiply(1 + (line?.vatPct ?? 0))
            total = new ObjectNumber(unitPriceWithVAT.toNumber()).multiply(line.quantity)
            applyDiscount(total, discount, document)
            vat = total.copy().subtract(total.copy().divide(1 + (line?.vatPct ?? 0)))
            return { total, vat, unitPrice, discountAmount }
          },
        },
        preVAT: () => {
          // grossTotal = ROUND((unitPrice x quantity - discount) x ( 1 + VAT%))
          const tempTotal = lineTotalPreVAT.copy()
          document === DocumentTypes.CREDITNOTE ? tempTotal.add(discountAmount) : tempTotal.subtract(discountAmount)
          vat = tempTotal.copy().multiply(line?.vatPct ?? 0)
          total = tempTotal.copy().add(vat)
          return { total, vat, unitPrice, discountAmount }
        },
      },
      hasDiscountPercent: {
        defFieldIsPrice: () => {
          // grossTotal = ROUND(unitPrice x quantity x ( 1 + VAT%) x (1 - discountPct))
          vat = lineTotalPreVAT
            .copy()
            .multiply(one.copy().subtract(discount.copy().divide(100)))
            .multiply(line?.vatPct ?? 0)
          total = lineTotalPreVAT
            .copy()
            .multiply(one.copy().subtract(discount.copy().divide(100)))
            .multiply(1 + (line?.vatPct ?? 0))
          discountAmount = lineTotalPreVAT
            .copy()
            .multiply(discount.copy().divide(100))
            .multiply(1 + (line?.vatPct ?? 0))
          return { total, vat, unitPrice, discountAmount }
        },
        defFieldIsTotal: () => {
          // grossTotal = ROUND(unitPrice x ( 1 + VAT%)) x quantity x (1 - discountPct))
          vat = lineTotalPreVAT
            .copy()
            .multiply(one.copy().subtract(discount.copy().divide(100)))
            .multiply(line?.vatPct ?? 0)
          const unitTotal = unitPrice.copy().multiply(1 + (line?.vatPct ?? 0))
          total = new ObjectNumber(unitTotal.toNumber())
            .multiply(line.quantity)
            .multiply(one.copy().subtract(discount.copy().divide(100)))
          discountAmount = lineTotalPreVAT
            .copy()
            .multiply(discount.copy().divide(100))
            .multiply(1 + (line?.vatPct ?? 0))
          return { total, vat, unitPrice, discountAmount }
        },
        preVat: () => {
          // grossTotal = ROUND((unitPrice x quantity x (1 - discount)) x ( 1 + VAT%))
          const tempTotal = lineTotalPreVAT.copy()
          const discountAmount = tempTotal.copy().multiply(discount.copy().divide(100))
          document === DocumentTypes.CREDITNOTE ? tempTotal.add(discountAmount) : tempTotal.subtract(discountAmount)
          vat = tempTotal.copy().multiply(line?.vatPct ?? 0)
          total = tempTotal.copy().add(vat)
          return { total, vat, unitPrice, discountAmount }
        },
      },
      doesNotHaveDiscountPerLine: {
        defFieldIsPrice: () => {
          // grossTotal = ROUND(unitPrice x quantity x ( 1 + VAT%))
          vat = lineTotalPreVAT.copy().multiply(line?.vatPct ?? 0)
          total = lineTotalPreVAT.copy().add(vat.toNumber())
          return { total, vat, unitPrice, discountAmount }
        },
        defFieldIsTotal: () => {
          // grossTotal = ROUND(unitPrice x ( 1 + VAT%)) x quantity
          vat = lineTotalPreVAT.copy().multiply(line?.vatPct ?? 0)
          const unitPriceWithVAT = unitPrice.copy().multiply(1 + (line?.vatPct ?? 0))
          total = new ObjectNumber(unitPriceWithVAT.toNumber()).multiply(line.quantity)

          return { total, vat, unitPrice, discountAmount }
        },
      },
    },
  }
}

/**
 *
 * @param line
 * @param fields
 * @param document
 */
const calculateLineValuesForDefFieldNotGrossTotal = (
  line: LineInterface,
  fields: DraftFieldsInterface,
  document?: DocumentType,
) => {
  if (perLineDiscountEnabled(fields) && line.discount > 0) {
    if (amountDiscountTypeAndAppliedPostVAT(fields)) {
      if (defFieldIsPrice(line)) {
        return calculateLineValues(line, fields, document).when.hasDiscountAmount.postVAT.defFieldIsPrice()
      } else {
        return calculateLineValues(line, fields, document).when.hasDiscountAmount.postVAT.defFieldIsTotal()
      }
    }
    if (amountDiscountTypeAndAppliedPreVAT(fields)) {
      return calculateLineValues(line, fields, document).when.hasDiscountAmount.preVAT()
    }
    if (percentDiscountTypeAndAppliedPostVAT(fields)) {
      if (defFieldIsPrice(line)) {
        return calculateLineValues(line, fields, document).when.hasDiscountPercent.defFieldIsPrice()
      } else {
        return calculateLineValues(line, fields, document).when.hasDiscountPercent.defFieldIsTotal()
      }
    }
    if (percentDiscountTypeAndAppliedPreVAT(fields)) {
      return calculateLineValues(line, fields, document).when.hasDiscountPercent.preVat()
    }
  } else {
    // no discount at all or discount per draft
    if (defFieldIsPrice(line)) {
      return calculateLineValues(line, fields, document).when.doesNotHaveDiscountPerLine.defFieldIsPrice()
    } else {
      return calculateLineValues(line, fields, document).when.doesNotHaveDiscountPerLine.defFieldIsTotal()
    }
  }
}

/**
 *
 *
 * @export
 * @param {LineInterface} line
 * @param {DraftFieldsInterface} fields
 * @param {String} [document]
 */
export function calculateValuesForLine(line: LineInterface, fields: DraftFieldsInterface, document?: DocumentType) {
  let lineValues
  const pricingModel = line.pricingModel || line.defField
  if (pricingModel === defFields.GROSSTOTAL) {
    lineValues = calculateLineValuesForDefFieldGrossTotal(line, fields, document)
  } else {
    lineValues = calculateLineValuesForDefFieldNotGrossTotal(line, fields, document)
  }

  line.netTotal = ObjectNumber.fromObjectNumber(lineValues.total).subtract(lineValues.vat.toNumber()).toNumber()
  line.taxTotal = lineValues.vat.toNumber()
  line.unitPrice = lineValues.unitPrice.toNumber(UNIT_PRICE_DECIMAL_PLACES)
  line.grossTotal = lineValues.total.toNumber()
  line.totalDiscount = lineValues.discountAmount ? lineValues.discountAmount.toNumber() : 0
}

export function isZeroVatInvoiceType(invoiceTypeId?: string | number) {
  return ZERO_VAT_INVOICE_TYPE_IDS.includes(String(invoiceTypeId))
}
